import type {
	ComparisonOperatorDTO,
	DataRequestDTO,
	DataProviderRequestDTO,
	DistributionRequestDTO,
	OperatorDTO,
	ParameterizedConceptDTO,
	SmartWorkspaceDTO,
	SmartWorkspaceDTOChart,
} from "@eu.mdotm.api/smart-workspaces";
import { Result } from "@mdotm/utils/runtime";
import type { ColumnState } from "ag-grid-community";
import z from "zod";
import { withChartSeriesRequest } from "../chart-type";
import { filtersToPredicate } from "../ctx/adapter";
import type { ParameterizedConceptDTOWithMandatoryUuid, Workspace, WorkspaceChartCtx } from "../ctx/context";
import { mockedNameConcept, removeUnusedParameterizedConcept } from "../repository";
import { type WorkspaceState, privateConcepts, resolveConcepts, sharedConcepts } from "./state";

/**
 * The read half. Every one of the ~35 subscription sites in the area reads through one of these, so that
 * "which shape does a view see" is answered here and not by whichever atom happened to hold it.
 *
 * Deliberately **plain functions of state**, not atoms. Two reasons: they stay testable without a store,
 * and the read-only-ness cannot be lost by accident — `deriveNestedAtom(base$, key)` returns a
 * read-*write* atom, which is how a component would rebuild today's eighteen bypassing writes with nicer
 * syntax. Commit 5 wraps these in narrow derived atoms (`atom((get) => selectCharts(get(state$)))`,
 * compared with `atomWithCompare`), which is where the render-subscription granularity comes from — one
 * undivided subscription would re-render the AG-Grid table on every keystroke of the search box.
 */

// --- Content -------------------------------------------------------------------------------------

export function selectView(state: WorkspaceState): Workspace["view"] {
	return state.content.view;
}

export function selectFilters(state: WorkspaceState): Workspace["filters"] {
	return state.content.filters;
}

export function selectFilter(state: WorkspaceState, parameterizedConceptUuid: string) {
	return state.content.filters.find((filter) => filter.parameterizedConceptUuid === parameterizedConceptUuid);
}

export function selectColumns(state: WorkspaceState): Workspace["columns"] {
	return state.content.columns;
}

export function selectColumn(state: WorkspaceState, parameterizedConceptUuid: string): ColumnState | undefined {
	return state.content.columns?.find((column) => column.colId === parameterizedConceptUuid);
}

/**
 * The columns whose `colId` is a parameterized-concept uuid, i.e. the ones that render a concept.
 *
 * The grid also carries columns of its own (checkbox, actions), and they must not be mistaken for
 * projected concepts — the uuid shape is what tells them apart, as it does today in `useWorkspaceColumns`.
 */
export function selectConceptColumns(state: WorkspaceState): ColumnState[] | undefined {
	return state.content.columns?.filter((column) =>
		Result.fromThrower(() => z.string().uuid().parse(column.colId)).isOk(),
	);
}

export function selectCharts(state: WorkspaceState): WorkspaceChartCtx[] {
	return state.content.charts;
}

export function selectChart(state: WorkspaceState, index: number): WorkspaceChartCtx | undefined {
	return index < 0 ? undefined : state.content.charts.at(index);
}

export function selectSort(state: WorkspaceState): Workspace["sort"] {
	return state.content.sort;
}

// --- Concepts ------------------------------------------------------------------------------------

/** The whole pool, private then shared, deduplicated — today's `allParametrizedConcepts$`. */
export function selectConcepts(state: WorkspaceState): ParameterizedConceptDTOWithMandatoryUuid[] {
	return resolveConcepts(state.concepts);
}

/** The half that rides the workspace PUT. */
export function selectPrivateConcepts(state: WorkspaceState): ParameterizedConceptDTOWithMandatoryUuid[] {
	return privateConcepts(state.concepts);
}

/** The half that has its own API and outlives the workspace. */
export function selectSharedConcepts(state: WorkspaceState): ParameterizedConceptDTOWithMandatoryUuid[] {
	return sharedConcepts(state.concepts);
}

export function selectConcept(
	state: WorkspaceState,
	uuid: string | undefined,
): ParameterizedConceptDTOWithMandatoryUuid | undefined {
	return uuid == null ? undefined : state.concepts.find((entry) => entry.concept.uuid === uuid)?.concept;
}

/** `true` when the concept persists through the shared-concept API rather than the workspace document. */
export function selectIsShared(state: WorkspaceState, uuid: string): boolean {
	return state.concepts.some((entry) => entry.origin === "STANDARD" && entry.concept.uuid === uuid);
}

// --- Data requests -------------------------------------------------------------------------------

/**
 * The one place a data request is assembled.
 *
 * Seven requests did the same four things by hand — trim the pool to what the surface projects, append the
 * name concept when the workspace has none, attach the predicate, attach the provider — across the table
 * (twice, the grid and the export), the five chart items and the bulk "Select all". That is a derivation
 * from state, so it is a selector: the surfaces say what they project and what tail their endpoint takes,
 * and nothing else about a request is a thing a view has to remember.
 *
 * Two requests in the area are deliberately *not* built here, because neither derives from the workspace:
 * the key-metric threshold resolves one named concept against its own provider, and
 * `getWorkspaceNestedCrumbs` runs before there is a store at all, off a document it just fetched.
 *
 * The request is also the react-query cache key (`flattenProperties` over the whole config for a
 * `fetchQuery`, over its parts for the `useQuery` sites), so the **field order here is load-bearing** and
 * matches what the three surfaces built: concepts, provider, projection, predicate, then the tail. A key
 * that reordered them, or gained an `undefined` where a field used to be absent, would be a different
 * cache entry — a silent refetch, or two components writing over one another.
 */

/** The `conceptId` of the one concept the search box can compare against. */
const nameConceptId = "systemId: name";

const dataProviderId = "investment" as const;

/** The workspace's own name concept, when it holds one. */
function nameConcept(state: WorkspaceState): ParameterizedConceptDTOWithMandatoryUuid | undefined {
	return selectConcepts(state).find((concept) => concept.conceptId === nameConceptId);
}

/**
 * The uuid the search predicate is written against: the workspace's own name concept when it has one,
 * {@link mockedNameConcept}'s otherwise — which is also the uuid the appended mock carries, so the
 * predicate always names a concept the request sends.
 */
export function selectNameConceptUuid(state: WorkspaceState): string {
	return nameConcept(state)?.uuid ?? mockedNameConcept.uuid;
}

/**
 * The predicate a request filters by: the workspace's filters, plus the search box's name match.
 *
 * The two halves are separate because only the first is persisted — `SmartWorkspaceDTO.predicate` is the
 * filters and nothing else, which is why the search box can never be saved into a workspace by accident.
 */
export function selectDataPredicate(state: WorkspaceState): OperatorDTO | undefined {
	const search = selectSearch(state);
	const filters = filtersToPredicate(selectFilters(state));
	if (!search) {
		return filters;
	}
	// `parameterizedConceptUuid` is the only optional field of a `ComparisonOperatorDTO` and the one that
	// says *what* is being compared, so the `keyof` guard is what stops a search from matching everything.
	const contains = {
		type: "COMPARISON" as const,
		keyword: "contains",
		parameterizedConceptUuid: selectNameConceptUuid(state),
		arguments: [search as unknown as object],
	} satisfies Record<keyof ComparisonOperatorDTO, unknown>;
	return !filters ? contains : { type: "LOGICAL", keyword: "and", conditions: [contains, filters] };
}

/**
 * The pool trimmed to what a request needs: what the surface projects, what the predicate names, and what
 * the sort names. Everything else is dropped — a request that carried the whole pool would make the
 * backend resolve every concept in the workspace to answer a question about three of them.
 */
export function selectRequestConcepts(
	state: WorkspaceState,
	projection: string[] | undefined,
): ParameterizedConceptDTOWithMandatoryUuid[] {
	return removeUnusedParameterizedConcept(
		selectConcepts(state),
		projection,
		selectDataPredicate(state),
		selectSort(state),
	);
}

/** The fields every data request carries, whichever endpoint it goes to. */
export type DataRequestBase = Pick<DataRequestDTO, "concepts" | "dataProvider" | "projection" | "predicate">;

/**
 * What is left of a request once {@link DataRequestBase} is taken out: the row endpoint's paging and
 * sorting, or the distribution endpoint's binning. Taken as `Omit` of the two DTOs rather than restated,
 * so neither can gain a field this cannot carry.
 */
export type DataRequestTail =
	| Omit<DataRequestDTO, keyof DataRequestBase>
	| Omit<DistributionRequestDTO, keyof DataRequestBase>;

export type DataRequestTarget<TTail extends DataRequestTail> = {
	/** The parameterized-concept uuids the surface projects: the grid's projection, or a chart's series. */
	projection: string[] | undefined;
	/**
	 * The chart these series belong to, when the caller is a chart item: its kind decides the request-time
	 * form of the projected concepts — a breakdown for the stacked column, a time series for the line. Only
	 * the chart's own series are shaped; see {@link withChartSeriesRequest}.
	 */
	chart?: Pick<SmartWorkspaceDTOChart, "chartType" | "series" | "breakdownProperties">;
	/**
	 * Whether the response is row-shaped and therefore has to carry something to label a row by. `false`
	 * for the distribution endpoint, which answers with bins: it has no rows to name, and has never sent
	 * the name concept.
	 */
	named?: boolean;
	/** The endpoint-specific tail: `limit`/`offset`/`sorting`, or `binning`. */
	extras: TTail;
};

export function buildDataRequest<TTail extends DataRequestTail>(
	state: WorkspaceState,
	target: DataRequestTarget<TTail>,
): DataRequestBase & TTail {
	const projected = selectRequestConcepts(state, target.projection);
	const shaped = target.chart ? withChartSeriesRequest(projected, target.chart) : projected;
	const named = target.named ?? true;
	const concepts: ParameterizedConceptDTO[] = named && !nameConcept(state) ? [...shaped, mockedNameConcept] : shaped;
	const base = {
		concepts,
		dataProvider: { dataProviderId, parameters: {} } satisfies Record<keyof DataProviderRequestDTO, unknown>,
		projection: target.projection ?? [],
		predicate: selectDataPredicate(state),
	} satisfies Record<keyof DataRequestBase, unknown>;

	// The cast is the spread: a generic `TTail` could in principle redeclare one of the four fields above, so
	// TypeScript will not conclude on its own that the result still has them. `DataRequestTail` is an `Omit`
	// of the two DTOs, so in practice it cannot.
	return { ...base, ...target.extras } as DataRequestBase & TTail;
}

// --- UI ------------------------------------------------------------------------------------------

export function selectSearch(state: WorkspaceState): string {
	return state.ui.search;
}

export function selectSelection(state: WorkspaceState): string[] {
	return state.ui.selection;
}

/** The `{page, size, total}` triple the table's pagination control reads, reassembled from both slices. */
export function selectPagination(state: WorkspaceState): Workspace["pagination"] {
	return { page: state.ui.page, size: state.content.pageSize, total: state.ui.resultTotal };
}

// --- Meta ----------------------------------------------------------------------------------------

/** The upstream document, for the fields the client does not model: uuid, name, taxonomy, acl, status. */
export function selectWorkspaceDocument(state: WorkspaceState): SmartWorkspaceDTO | undefined {
	return state.meta.document;
}

export function selectWorkspaceUuid(state: WorkspaceState): string | undefined {
	return state.meta.document?.uuid;
}
